<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue'
import { Combobox, createListCollection } from '@ark-ui/vue'
import UiIcon from './UiIcon.vue'
import UiFieldContainer from './UiFieldContainer.vue'
import type { UiSelectOption } from './UiSelect.vue'
import UiComboboxGroup from './UiComboboxGroup.vue'
import type { ComboboxGroupData } from './types/combobox'

export interface UiMultiSelectComboboxProps {
    name?: string
    id?: string
    options: UiSelectOption[]
    optionGroups?: ComboboxGroupData[]
    placeholder?: string
    disabled?: boolean
    size?: 'md' | 'lg'
    hint?: string
    hintContent?: string
    error?: string
    invalid?: boolean
    showPlaceholderWhenSelected?: boolean
    max?: number
    openOnClick?: boolean
    showChevron?: boolean
}

const props = withDefaults(defineProps<UiMultiSelectComboboxProps>(), {
    options: () => [],
    optionGroups: () => [],
    size: 'md',
    showPlaceholderWhenSelected: true,
    openOnClick: false,
    showChevron: false,
})

const model = defineModel<string[]>({ default: [] })

const emit = defineEmits<{
    (e: 'validate'): void
}>()

const isOpen = ref(false)
const isFocused = ref(false)
const query = ref<string>('')

// Internal value to track the combobox state
const internalValue = ref<string[]>(model.value)

const groupedOptions = computed<ComboboxGroupData[]>(() => {
    if (props.optionGroups.length) return props.optionGroups
    return [{
        id: 'default',
        label: '',
        items: props.options.map(opt => ({
            value: opt.value,
            label: opt.label,
        })),
    }]
})

const filteredGroups = computed<ComboboxGroupData[]>(() => {
    if (!query.value) return groupedOptions.value
    const q = query.value.toLowerCase()
    return groupedOptions.value
        .map(group => ({
            ...group,
            items: group.items.filter(item =>
                item.label.toLowerCase().includes(q) || item.value.toLowerCase().includes(q),
            ),
        }))
        .filter(group => group.items.length > 0)
})

const filteredOptions = computed(() =>
    filteredGroups.value.flatMap(group => group.items.map(item => ({
        label: item.label,
        value: item.value,
    }))),
)

const collection = computed(() => createListCollection({
    items: filteredOptions.value,
    itemToValue: (item: UiSelectOption) => item.value,
    itemToString: (item: UiSelectOption) => item.label,
}))

const overflowPadding = computed(() => {
    if (typeof window === 'undefined') return 0
    const safeAreaTop = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top')
    const safeAreaBottom = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-bottom')
    const top = parseInt(safeAreaTop) || 0
    const bottom = parseInt(safeAreaBottom) || 0
    return Math.max(top, bottom)
})

const isMaxReached = computed(() => {
    if (!props.max) return false
    return internalValue.value.length >= props.max
})

function isItemDisabled(itemValue: string) {
    if (internalValue.value.includes(itemValue)) return false
    return isMaxReached.value
}

function getGroupItems(groupId: string) {
    return filteredGroups.value.find(group => group.id === groupId)?.items || []
}

// Sync external model to internal value
watch(() => model.value, (newValue) => {
    internalValue.value = newValue
}, { immediate: true })

// Handle value changes with max limit enforcement
function handleValueChange(newValue: string[]) {
    const activeQuery = query.value

    if (props.max && newValue.length > props.max) {
        // Prevent exceeding max - keep only the allowed selections
        // If adding new items, keep the first max items
        const limitedValue = newValue.slice(0, props.max)
        internalValue.value = limitedValue
        model.value = limitedValue
    } else {
        internalValue.value = newValue
        model.value = newValue
    }

    if (activeQuery && isOpen.value) {
        nextTick(() => {
            query.value = activeQuery
        })
    }

    emit('validate')
}

// Force repositioning when dropdown opens to fix SSR positioning issues
// Use requestAnimationFrame after nextTick to ensure lazy-mounted content is rendered
watch(isOpen, (newVal) => {
    if (newVal && typeof window !== 'undefined') {
        nextTick(() => {
            requestAnimationFrame(() => {
                window.dispatchEvent(new Event('resize'))
            })
        })
    }
})
</script>

<template>
    <UiFieldContainer :size="props.size" :invalid="props.invalid" :hint="props.hint" :hintContent="props.hintContent" :error="props.error" :disabled="props.disabled"
        :describedByIdBase="props.id || props.name" :isFocused="isFocused">
        <Combobox.Root
            :positioning="{
                boundary: 'clippingAncestors',
                fitViewport: true,
                gutter: 16,
                sameWidth: true,
                overflowPadding: overflowPadding,
            }"
            class="w-full flex-1" :collection="collection" :model-value="internalValue" @update:model-value="handleValueChange" :multiple="true" :disabled="props.disabled"
            :id="props.id || props.name" v-model:open="isOpen" v-model:inputValue="query" inputBehavior="autohighlight" :openOnClick="props.openOnClick" lazyMount>
            <Combobox.Control class="w-full flex items-center gap-2">
                <Combobox.Input 
                    :placeholder="props.placeholder || 'Search...'" 
                    class="flex-1 text-md se-font-input font-archivo bg-transparent outline-none"
                    autocomplete="off"
                    @focus="isFocused = true"
                    @blur="isFocused = false"
                    :disabled="props.disabled"
                    :aria-invalid="props.invalid ? 'true' : undefined"
                    :aria-describedby="props.error ? (props.id || props.name) + '-error' : (props.hint ? (props.id || props.name) + '-hint' : undefined)"
                />
                <UiIcon
                    v-if="props.showChevron"
                    name="chevron-down"
                    class="h-4 w-4 shrink-0 transition-transform"
                    :class="[
                        props.disabled ? 'text-se-gray-400' : 'text-se-gray-700',
                        isOpen ? 'rotate-180' : '',
                    ]"
                />
            </Combobox.Control>
            <Teleport to="body" defer>
                <Combobox.Positioner class="!max-w-none"
                :class="{
                    '!w-[calc(var(--reference-width)+var(--spacing)*16)] -ml-16 md:ml-0': props.size === 'lg',
                    '!w-[calc(var(--reference-width)+var(--spacing)*8)] -ml-8 md:ml-0': props.size === 'md',
                }">
                    <Combobox.Content
                        class="border-1 min-h-[200px] max-h-[min(var(--available-height),400px)] border-se-gray-300 rounded-md shadow-md bg-se-white overflow-x-hidden z-100">

                        <Combobox.Empty>
                            <div class="px-4 py-2 text-se-gray-500">No results found</div>
                        </Combobox.Empty>

                        <template v-for="group in filteredGroups" :key="group.id">
                            <UiComboboxGroup v-if="group.label" :id="group.id" :label="group.label">
                                <Combobox.Item
                                    v-for="item in getGroupItems(group.id)"
                                    :key="item.value"
                                    :item="item"
                                    class="flex items-center justify-between py-2"
                                    :class="{
                                        'px-8': props.size === 'lg',
                                        'px-4': props.size === 'md',
                                        'cursor-pointer hover:bg-se-gray-100 data-[highlighted]:bg-se-gray-100': !isItemDisabled(item.value),
                                        'opacity-50 cursor-not-allowed text-se-gray-400': isItemDisabled(item.value),
                                    }"
                                >
                                    <Combobox.ItemText>{{ item.label }}</Combobox.ItemText>
                                    <div class="flex items-center gap-2">
                                        <Combobox.ItemIndicator>
                                            <div class="inline-flex items-center justify-center w-4 h-4 rounded border-1 border-se-primary bg-se-primary">
                                                <UiIcon name="check" class="w-3 h-3 text-se-white" />
                                            </div>
                                        </Combobox.ItemIndicator>
                                        <div v-if="!internalValue.includes(item.value)" class="inline-flex items-center justify-center w-4 h-4 rounded border-1 border-se-gray-300">
                                        </div>
                                    </div>
                                </Combobox.Item>
                            </UiComboboxGroup>

                            <template v-else>
                                <Combobox.Item
                                    v-for="item in getGroupItems(group.id)"
                                    :key="item.value"
                                    :item="item"
                                    class="flex items-center justify-between py-2"
                                    :class="{
                                        'px-8': props.size === 'lg',
                                        'px-4': props.size === 'md',
                                        'cursor-pointer hover:bg-se-gray-100 data-[highlighted]:bg-se-gray-100': !isItemDisabled(item.value),
                                        'opacity-50 cursor-not-allowed text-se-gray-400': isItemDisabled(item.value),
                                    }"
                                >
                                    <Combobox.ItemText>{{ item.label }}</Combobox.ItemText>
                                    <div class="flex items-center gap-2">
                                        <Combobox.ItemIndicator>
                                            <div class="inline-flex items-center justify-center w-4 h-4 rounded border-1 border-se-primary bg-se-primary">
                                                <UiIcon name="check" class="w-3 h-3 text-se-white" />
                                            </div>
                                        </Combobox.ItemIndicator>
                                        <div v-if="!internalValue.includes(item.value)" class="inline-flex items-center justify-center w-4 h-4 rounded border-1 border-se-gray-300">
                                        </div>
                                    </div>
                                </Combobox.Item>
                            </template>
                        </template>
                    </Combobox.Content>
                </Combobox.Positioner>
            </Teleport>
        </Combobox.Root>
    </UiFieldContainer>
</template>
